// src/pages/CompleteProfile.js
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { doc, setDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../services/firebase";
import { signOut } from "firebase/auth";
import { Link } from "react-router-dom";
import API_BASE from "../api/apiBase";

export default function CompleteProfile() {
  const navigate = useNavigate();
  const location = useLocation();

  const { uid, email, name: googleName, fromGoogle } = location.state || {};

  const [role, setRole] = useState("homeowner");
  const [companyName, setCompanyName] = useState("");
  const [name, setName] = useState(googleName || "");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Guard
  if (!uid || !email) {
    return (
      <div style={styles.errorWrap}>
        <img src="/logo.png" alt="MeasureSmart AI" style={styles.logo} />
        <h2>Session expired</h2>
        <p>Please sign in again to complete your profile.</p>
        <button
          style={styles.btnPrimary}
          onClick={() => navigate("/", { replace: true })}
        >
          Back to Sign In
        </button>
      </div>
    );
  }

  const handleSubmit = async () => {
    setError(null);

    if (!name.trim()) {
      setError("Please enter your full name.");
      return;
    }

    if (role === "contractor" && !companyName.trim()) {
      setError("Company name is required for contractors.");
      return;
    }

    setLoading(true);

    try {
      let contractorOrgId = null;

      if (role === "contractor") {
        // Create contractor org doc
        // subscriptionStatus starts as "incomplete" — 
        // Stripe webhook flips to "trialing" after card collected
        const orgRef = await addDoc(
          collection(db, "contractorOrganizations"),
          {
            name: companyName.trim(),
            stripeCustomerId: null,
            stripeSubscriptionId: null,
            plan: "base",
            subscriptionStatus: "incomplete",  // ← not trialing until Stripe confirms
            trialStartedAt: null,              // ← set by Stripe webhook
            trialEndsAt: null,                 // ← set by Stripe webhook
            currentPeriodEnd: null,
            // Extensible fields for future collection
            licenseNumber: null,
            licenseState: null,
            licenseExpiry: null,
            insuranceCarrier: null,
            insurancePolicyNumber: null,
            insuranceExpiry: null,
            serviceArea: [],
            trades: [],
            createdAt: serverTimestamp(),
          }
        );
        contractorOrgId = orgRef.id;
      }

      // Create user doc
      await setDoc(
        doc(db, "users", uid),
        {
          email,
          name: name.trim(),
          phone: phone.trim() || null,
          role,
          contractorOrgId: contractorOrgId || null,
          isOrgAdmin: role === "contractor",
          profileCompletedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          fromGoogle: fromGoogle || false,
        },
        { merge: true }
      );

      // Route based on role
      if (role === "homeowner") {
        navigate("/measure", { replace: true });
        return;
      }

      // Contractor — redirect to Stripe checkout immediately
      // Card collected but not charged for 90 days
      try {
        const idToken = await auth.currentUser.getIdToken();

        const res = await fetch(`${API_BASE}/api/stripe/create-checkout-session`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ testMode: false }),
        });

        const data = await res.json();

        if (data?.url) {
          window.location.href = data.url;
          return;
        }

        // If Stripe fails — still let them in but show incomplete state
        console.error("Stripe checkout failed:", data);
        navigate("/contractor", { replace: true });

      } catch (stripeErr) {
        console.error("Stripe redirect error:", stripeErr);
        // Fail gracefully — contractor gets in but dashboard shows soft block
        navigate("/contractor", { replace: true });
      }

    } catch (err) {
      console.error("CompleteProfile error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      await signOut(auth);
    } catch (_) {}
    navigate("/", { replace: true });
  };

  return (
    <div style={styles.page}>

      {/* Minimal header */}
      <header style={styles.header}>
        <Link to="/">
          <img src="/logo.png" alt="MeasureSmart AI" style={styles.logo} />
        </Link>
      </header>

      <div style={styles.card}>

        {/* Progress indicator */}
        <div style={styles.progressWrap}>
          <div style={styles.progressStep}>
            <div style={styles.progressDot}>✓</div>
            <span style={styles.progressLabel}>Sign in with Google</span>
          </div>
          <div style={styles.progressLine} />
          <div style={styles.progressStep}>
            <div style={{ ...styles.progressDot, ...styles.progressDotActive }}>2</div>
            <span style={{ ...styles.progressLabel, fontWeight: 700, color: "#003366" }}>
              Complete profile
            </span>
          </div>
          <div style={styles.progressLine} />
          <div style={styles.progressStep}>
            <div style={{ ...styles.progressDot, ...styles.progressDotInactive }}>3</div>
            <span style={{ ...styles.progressLabel, color: "#9ca3af" }}>
              {role === "contractor" ? "Start free trial" : "Get started"}
            </span>
          </div>
        </div>

        <h2 style={styles.title}>Tell us about yourself</h2>
        <p style={styles.sub}>
          Signed in as <strong>{email}</strong>
        </p>

        {/* Role selection */}
        <div style={styles.roleRow}>
          <button
            style={{
              ...styles.roleBtn,
              ...(role === "homeowner" ? styles.roleBtnActive : {}),
            }}
            onClick={() => setRole("homeowner")}
          >
            ��� Homeowner
          </button>
          <button
            style={{
              ...styles.roleBtn,
              ...(role === "contractor" ? styles.roleBtnActive : {}),
            }}
            onClick={() => setRole("contractor")}
          >
            ��� Contractor
          </button>
        </div>

        {/* Role description */}
        <p style={styles.roleDesc}>
          {role === "homeowner"
            ? "Measure surfaces and share projects with contractors. Free forever."
            : "Receive homeowner measurements and submit estimates. Free for 90 days, then $1,200/year. Credit card required to start trial — no charge today."}
        </p>

        {/* Fields */}
        <div style={styles.fieldGroup}>
          <label style={styles.label}>Full Name *</label>
          <input
            style={styles.input}
            type="text"
            placeholder="Your full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
          />
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label}>Phone (optional)</label>
          <input
            style={styles.input}
            type="tel"
            placeholder="Your phone number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
          />
        </div>

        {role === "contractor" && (
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Company Name *</label>
            <input
              style={styles.input}
              type="text"
              placeholder="Your company name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
            <p style={styles.fieldNote}>
              This will appear on your contractor profile visible to homeowners.
            </p>
          </div>
        )}

        {/* Contractor trial notice */}
        {role === "contractor" && (
          <div style={styles.trialNotice}>
            <div style={styles.trialNoticeTitle}>��� 90-Day Free Trial</div>
            <div style={styles.trialNoticeText}>
              Full contractor access for 90 days. Your card will not be charged
              until your trial ends. Cancel anytime before day 90.
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={styles.errorMsg}>{error}</div>
        )}

        {/* Actions */}
        <button
          style={{
            ...styles.btnPrimary,
            opacity: loading ? 0.7 : 1,
            cursor: loading ? "not-allowed" : "pointer",
          }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading
            ? role === "contractor"
              ? "Setting up — redirecting to payment..."
              : "Setting up your account..."
            : role === "contractor"
            ? "Continue to Free Trial →"
            : "Complete Setup →"}
        </button>

        <button style={styles.btnCancel} onClick={handleCancel}>
          Cancel — sign out
        </button>

      </div>
    </div>
  );
}

// Styles
const styles = {
  page: {
    minHeight: "100vh",
    background: "#f5f9ff",
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  header: {
    background: "#ffffff",
    borderBottom: "1px solid #dfe8f5",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    boxShadow: "0 2px 10px rgba(0,40,90,0.06)",
  },
  logo: {
    height: 48,
    width: "auto",
  },
  card: {
    maxWidth: 480,
    margin: "48px auto",
    background: "#ffffff",
    borderRadius: 16,
    padding: "36px 40px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.10)",
    border: "1px solid #e8f0fb",
  },
  progressWrap: {
    display: "flex",
    alignItems: "center",
    marginBottom: 28,
  },
  progressStep: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
  },
  progressDot: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "#0057b8",
    color: "#fff",
    fontSize: 12,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  progressDotActive: {
    background: "#0057b8",
    boxShadow: "0 0 0 4px rgba(0,87,184,0.18)",
  },
  progressDotInactive: {
    background: "#e5e7eb",
    color: "#9ca3af",
  },
  progressLine: {
    flex: 1,
    height: 2,
    background: "#e5e7eb",
    margin: "0 8px",
    marginBottom: 20,
  },
  progressLabel: {
    fontSize: 11,
    color: "#6b7280",
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  title: {
    fontSize: "1.6rem",
    fontWeight: 900,
    color: "#003366",
    margin: "0 0 6px",
  },
  sub: {
    fontSize: "0.9rem",
    color: "#6b7280",
    marginBottom: 24,
  },
  roleRow: {
    display: "flex",
    gap: 12,
    marginBottom: 12,
  },
  roleBtn: {
    flex: 1,
    padding: "12px 16px",
    borderRadius: 10,
    border: "2px solid #e5e7eb",
    background: "#f9fafb",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    color: "#374151",
    transition: "all 0.15s",
  },
  roleBtnActive: {
    border: "2px solid #0057b8",
    background: "#eff6ff",
    color: "#003366",
  },
  roleDesc: {
    fontSize: "0.85rem",
    color: "#6b7280",
    marginBottom: 20,
    lineHeight: 1.5,
    padding: "8px 12px",
    background: "#f9fafb",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
  },
  fieldGroup: {
    marginBottom: 16,
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 700,
    color: "#374151",
    marginBottom: 6,
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 14,
    color: "#12263f",
    background: "#f9fafb",
    boxSizing: "border-box",
    outline: "none",
  },
  fieldNote: {
    fontSize: 12,
    color: "#9ca3af",
    marginTop: 4,
  },
  trialNotice: {
    background: "#ecfdf5",
    border: "1px solid #a7f3d0",
    borderRadius: 10,
    padding: "14px 16px",
    marginBottom: 20,
  },
  trialNoticeTitle: {
    fontWeight: 800,
    fontSize: 14,
    color: "#065f46",
    marginBottom: 4,
  },
  trialNoticeText: {
    fontSize: 13,
    color: "#047857",
    lineHeight: 1.5,
  },
  errorMsg: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 14,
    marginBottom: 16,
  },
  errorWrap: {
    maxWidth: 400,
    margin: "80px auto",
    textAlign: "center",
    padding: 24,
  },
  btnPrimary: {
    width: "100%",
    padding: "12px 16px",
    background: "#0057b8",
    color: "#ffffff",
    border: "none",
    borderRadius: 10,
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    marginBottom: 10,
    transition: "background 0.15s",
  },
  btnCancel: {
    width: "100%",
    padding: "10px 16px",
    background: "transparent",
    color: "#9ca3af",
    border: "none",
    borderRadius: 10,
    fontSize: 14,
    cursor: "pointer",
  },
};
